import React, { useMemo, useState } from 'react';
import {
  Button,
  Field,
  Input,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import {
  Add16Regular,
  Add20Regular,
  Building24Regular,
  Delete20Regular,
  DoorTag20Regular,
  Desktop20Regular,
  Edit20Regular,
  MoreHorizontal20Regular,
  Search20Regular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { Consultorio, QueueDisplayScreen, Sector, Sede } from '../../../../models/Entities/Turnero';
import { useOfficesStyles } from '../Offices.Styles';
import { countSectorDisplays, getSectorRoomIds, groupSedes } from '../Offices.Functions';
import { EmptyState, ListSkeleton } from '../OfficesCommon/OfficesCommon.Component';

const useSedesTabStyles = makeStyles({
  sedeRow: {
    backgroundColor: tokens.colorNeutralBackground2,
  },
  // Guía "Árbol": sangría de 26 px por nivel con guía vertical.
  sectorCell: {
    paddingLeft: '26px',
    borderLeft: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    marginLeft: tokens.spacingHorizontalM,
  },
});

export interface SedesTabProps {
  sedes: Sede[];
  sectores: Sector[];
  consultorios: Consultorio[];
  displays: QueueDisplayScreen[];
  isLoading: boolean;
  selectedSectorId?: number | null;
  onCreateSede: () => void;
  onEditSede: (sede: Sede) => void;
  onDeleteSede: (sede: Sede) => void;
  onCreateSector: (sedeId?: number) => void;
  onEditSector: (sector: Sector) => void;
  onDeleteSector: (sector: Sector) => void;
  onShowRooms: (sector: Sector) => void;
  onShowDisplays: (sector: Sector) => void;
}

const SedesTab = (props: SedesTabProps) => {
  const { sedes, sectores, consultorios, displays, isLoading, selectedSectorId } = props;
  const { t } = useTranslation();
  const styles = useOfficesStyles();
  const local = useSedesTabStyles();
  const [search, setSearch] = useState('');
  const groups = useMemo(() => groupSedes(sedes, sectores, search), [sedes, sectores, search]);

  const moreButton = (label: string) => (
    <MenuTrigger disableButtonEnhancement>
      <Button appearance="subtle" icon={<MoreHorizontal20Regular />} aria-label={label} />
    </MenuTrigger>
  );

  const renderContent = () => {
    if (isLoading && sedes.length === 0) return <ListSkeleton testId="sedes-skeleton" />;
    if (sedes.length === 0) {
      return (
        <EmptyState
          testId="sedes-empty"
          icon={<Building24Regular />}
          title={t('Offices.sede.emptyTitle')}
          description={t('Offices.sede.emptyText')}
          actionLabel={t('Offices.sede.create')}
          actionIcon={<Add16Regular />}
          onAction={props.onCreateSede}
        />
      );
    }
    if (groups.length === 0) {
      return <EmptyState icon={<Search20Regular />} title={t('Offices.noResultsTitle')} description={t('Offices.noResultsText')} />;
    }
    return (
      <div className={styles.surface}>
        <Table aria-label={t('Offices.tabs.structure')} className={styles.table} size="medium">
          <TableHeader>
            <TableRow>
              <TableHeaderCell className={styles.headerCell}>{t('Offices.columns.sedeSector')}</TableHeaderCell>
              <TableHeaderCell className={mergeClasses(styles.headerCell, styles.numericHeader)} style={{ width: '140px' }}>
                {t('Offices.columns.rooms')}
              </TableHeaderCell>
              <TableHeaderCell className={mergeClasses(styles.headerCell, styles.numericHeader)} style={{ width: '120px' }}>
                {t('Offices.columns.displays')}
              </TableHeaderCell>
              <TableHeaderCell className={styles.headerCell} style={{ width: '64px' }} aria-label={t('Offices.columns.actions')} />
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.map(({ sede, sectores: own }) => {
              const sedeRooms = own.reduce((acc, s) => acc + getSectorRoomIds(s, consultorios).length, 0);
              const sedeDisplays = displays.filter((d) => d.sedeId === sede.id || own.some((s) => s.id === d.sectorId)).length;
              return (
                <React.Fragment key={`sede-${sede.id}`}>
                  <TableRow className={mergeClasses(styles.row, local.sedeRow)} data-testid={`sede-row-${sede.id}`}>
                    <TableCell>
                      <div className={styles.primaryCell}>
                        <Button appearance="transparent" className={styles.linkButton} onClick={() => props.onEditSede(sede)}>
                          {sede.name}
                        </Button>
                        <span className={styles.secondaryText}>
                          {[sede.direccion, t('Offices.sede.sectorCount', { count: own.length })].filter(Boolean).join(' · ')}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className={styles.numericCell}>{sedeRooms}</TableCell>
                    <TableCell className={styles.numericCell}>{sedeDisplays}</TableCell>
                    <TableCell>
                      <div className={styles.actionsCell}>
                        <Menu>
                          {moreButton(t('Offices.actions.moreFor', { name: sede.name }))}
                          <MenuPopover>
                            <MenuList>
                              <MenuItem icon={<Add20Regular />} onClick={() => props.onCreateSector(sede.id)}>
                                {t('Offices.sector.create')}
                              </MenuItem>
                              <MenuItem icon={<Edit20Regular />} onClick={() => props.onEditSede(sede)}>
                                {t('Offices.actions.edit')}
                              </MenuItem>
                              <MenuItem icon={<Delete20Regular />} className={styles.dangerItem} onClick={() => props.onDeleteSede(sede)}>
                                {t('Offices.actions.delete')}
                              </MenuItem>
                            </MenuList>
                          </MenuPopover>
                        </Menu>
                      </div>
                    </TableCell>
                  </TableRow>
                  {own.map((sector) => {
                    const selected = selectedSectorId !== undefined && selectedSectorId !== null && sector.id === selectedSectorId;
                    return (
                      <TableRow
                        key={`sector-${sector.id}`}
                        className={mergeClasses(styles.row, selected && styles.rowSelected)}
                        aria-selected={selected}
                        data-testid={`sector-row-${sector.id}`}
                      >
                        <TableCell>
                          <div className={mergeClasses(styles.primaryCell, local.sectorCell)}>
                            <Button appearance="transparent" className={styles.linkButton} onClick={() => props.onEditSector(sector)}>
                              {sector.name}
                            </Button>
                            {sector.number && <span className={styles.secondaryText}>{sector.number}</span>}
                          </div>
                        </TableCell>
                        <TableCell className={styles.numericCell}>{getSectorRoomIds(sector, consultorios).length}</TableCell>
                        <TableCell className={styles.numericCell}>{countSectorDisplays(sector.id, displays)}</TableCell>
                        <TableCell>
                          <div className={styles.actionsCell}>
                            <Menu>
                              {moreButton(t('Offices.actions.moreFor', { name: sector.name }))}
                              <MenuPopover>
                                <MenuList>
                                  <MenuItem icon={<DoorTag20Regular />} onClick={() => props.onShowRooms(sector)}>
                                    {t('Offices.sector.showRooms')}
                                  </MenuItem>
                                  <MenuItem icon={<Desktop20Regular />} onClick={() => props.onShowDisplays(sector)}>
                                    {t('Offices.sector.showDisplays')}
                                  </MenuItem>
                                  <MenuItem icon={<Edit20Regular />} onClick={() => props.onEditSector(sector)}>
                                    {t('Offices.actions.edit')}
                                  </MenuItem>
                                  <MenuItem icon={<Delete20Regular />} className={styles.dangerItem} onClick={() => props.onDeleteSector(sector)}>
                                    {t('Offices.actions.delete')}
                                  </MenuItem>
                                </MenuList>
                              </MenuPopover>
                            </Menu>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {own.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <div className={mergeClasses(styles.primaryCell, local.sectorCell)}>
                          <span className={styles.secondaryText}>{t('Offices.sector.emptyForSede')}</span>
                          <div>
                            <Button appearance="transparent" size="small" icon={<Add16Regular />} onClick={() => props.onCreateSector(sede.id)}>
                              {t('Offices.sector.create')}
                            </Button>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <section className={styles.section} aria-label={t('Offices.tabs.structure')}>
      <div className={styles.toolbar}>
        <div className={styles.toolbarFilters}>
          <Field label={t('Offices.filters.search')}>
            <Input
              className={styles.search}
              contentBefore={<Search20Regular />}
              value={search}
              placeholder={t('Offices.sede.searchPlaceholder')}
              aria-label={t('Offices.filters.search')}
              onChange={(_, d) => setSearch(d.value)}
            />
          </Field>
        </div>
        <div className={styles.toolbarActions}>
          <Button appearance="secondary" icon={<Add16Regular />} onClick={() => props.onCreateSector()} disabled={sedes.length === 0}>
            {t('Offices.sector.create')}
          </Button>
          <Button appearance="primary" icon={<Add16Regular />} onClick={props.onCreateSede}>
            {t('Offices.sede.create')}
          </Button>
        </div>
      </div>
      {renderContent()}
    </section>
  );
};

export default SedesTab;
